import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MESI = [
  { v: '0', l: 'Tutto l\'anno' },
  { v: '1', l: 'Gennaio' }, { v: '2', l: 'Febbraio' }, { v: '3', l: 'Marzo' },
  { v: '4', l: 'Aprile' }, { v: '5', l: 'Maggio' }, { v: '6', l: 'Giugno' },
  { v: '7', l: 'Luglio' }, { v: '8', l: 'Agosto' }, { v: '9', l: 'Settembre' },
  { v: '10', l: 'Ottobre' }, { v: '11', l: 'Novembre' }, { v: '12', l: 'Dicembre' },
];

export default function RegistriIvaPage() {
  const currentYear = new Date().getFullYear();
  const [anno, setAnno] = useState(currentYear);
  const [mese, setMese] = useState('0');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const mParam = mese && mese !== '0' ? `&mese=${mese}` : '';
      const res = await axios.get(`${API}/fatturazione/registri-iva?anno=${anno}${mParam}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error('Errore caricamento registri IVA:', err);
      toast.error('Errore caricamento registri IVA');
    } finally { setLoading(false); }
  }, [anno, mese]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n) => (n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 });
  const years = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="space-y-6" data-testid="registri-iva-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <BookOpen className="h-6 w-6" />Registri IVA
          </h1>
          <p className="text-sm text-slate-500 mt-1">Liquidazione IVA per periodo</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(anno)} onValueChange={v => setAnno(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="iva-anno"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={mese} onValueChange={setMese}>
            <SelectTrigger className="w-44" data-testid="iva-mese"><SelectValue /></SelectTrigger>
            <SelectContent>{MESI.map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <p className="text-xs text-red-600 uppercase font-medium">IVA a Debito (vendite)</p>
                <p className="text-2xl font-bold text-red-700 mt-1" data-testid="iva-debito">€ {fmt(data.iva_a_debito)}</p>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-4">
                <p className="text-xs text-emerald-600 uppercase font-medium">IVA a Credito (acquisti)</p>
                <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="iva-credito">€ {fmt(data.iva_a_credito)}</p>
              </CardContent>
            </Card>
            <Card className={`${data.iva_da_versare >= 0 ? 'border-orange-200 bg-orange-50' : 'border-blue-200 bg-blue-50'}`}>
              <CardContent className="p-4">
                <p className={`text-xs uppercase font-medium ${data.iva_da_versare >= 0 ? 'text-orange-600' : 'text-blue-600'}`}>{data.iva_da_versare >= 0 ? 'IVA da Versare' : 'IVA a Credito (recuperabile)'}</p>
                <p className={`text-2xl font-bold mt-1 ${data.iva_da_versare >= 0 ? 'text-orange-700' : 'text-blue-700'}`} data-testid="iva-saldo">€ {fmt(Math.abs(data.iva_da_versare))}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">Registro Vendite (IVA a debito)</h3>
                  <p className="text-xs text-slate-500">Fatture emesse e NC emesse</p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs uppercase">Aliquota</TableHead>
                      <TableHead className="text-xs uppercase text-right">N.</TableHead>
                      <TableHead className="text-xs uppercase text-right">Imponibile</TableHead>
                      <TableHead className="text-xs uppercase text-right">IVA</TableHead>
                      <TableHead className="text-xs uppercase text-right">Totale</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.vendite.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">Nessun dato</TableCell></TableRow>
                    ) : data.vendite.map((r) => (
                      <TableRow key={`v-${r.aliquota}`}>
                        <TableCell className="font-mono">{r.aliquota}%</TableCell>
                        <TableCell className="text-right text-sm">{r.n_doc}</TableCell>
                        <TableCell className="text-right">€ {fmt(r.imponibile)}</TableCell>
                        <TableCell className="text-right">€ {fmt(r.iva)}</TableCell>
                        <TableCell className="text-right font-semibold">€ {fmt(r.totale)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-0">
                <div className="p-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">Registro Acquisti (IVA a credito)</h3>
                  <p className="text-xs text-slate-500">Fatture ricevute e NC ricevute</p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs uppercase">Aliquota</TableHead>
                      <TableHead className="text-xs uppercase text-right">N.</TableHead>
                      <TableHead className="text-xs uppercase text-right">Imponibile</TableHead>
                      <TableHead className="text-xs uppercase text-right">IVA</TableHead>
                      <TableHead className="text-xs uppercase text-right">Totale</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.acquisti.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">Nessun dato</TableCell></TableRow>
                    ) : data.acquisti.map((r) => (
                      <TableRow key={`a-${r.aliquota}`}>
                        <TableCell className="font-mono">{r.aliquota}%</TableCell>
                        <TableCell className="text-right text-sm">{r.n_doc}</TableCell>
                        <TableCell className="text-right">€ {fmt(r.imponibile)}</TableCell>
                        <TableCell className="text-right">€ {fmt(r.iva)}</TableCell>
                        <TableCell className="text-right font-semibold">€ {fmt(r.totale)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </>
      )}
      {loading && <div className="text-center py-6 text-slate-500">Caricamento...</div>}
    </div>
  );
}
